import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ShoppingBag, Trash2, ExternalLink, Shirt, Sparkles, Loader2, SlidersHorizontal, Heart, Share2, MessageCircle, Instagram, Link2, Download, Eye, Flame, Star, Globe, Copy, CornerDownRight, Send, UserCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import api, { fileUrl } from "@/lib/api";

const seasonOf = (style = "") => {
  const s = style.toLowerCase();
  if (s.includes("estiv")) return "Estate";
  if (s.includes("invern")) return "Inverno";
  if (s.includes("primaver")) return "Primavera";
  if (s.includes("autunn")) return "Autunno";
  return "Tutto l'anno";
};

export default function Wardrobe() {
  const [outfits, setOutfits] = useState(null);
  const [selected, setSelected] = useState(null);
  const [styleFilter, setStyleFilter] = useState("all");
  const [seasonFilter, setSeasonFilter] = useState("all");
  const [sortBy, setSortBy] = useState("recent");
  const [replyDrafts, setReplyDrafts] = useState({});
  const [replyingId, setReplyingId] = useState(null);
  const [showcaseOpen, setShowcaseOpen] = useState(false);
  const [sc, setSc] = useState(null);
  const [scSaving, setScSaving] = useState(false);
  const [followers, setFollowers] = useState([]);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const load = () => api.get("/outfits").then((r) => setOutfits(r.data)).catch(() => setOutfits([]));
  useEffect(() => { load(); }, []);

  useEffect(() => {
    if (searchParams.get("vetrina") === "1") {
      openShowcase();
      searchParams.delete("vetrina");
      setSearchParams(searchParams, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openShowcase = async () => {
    setShowcaseOpen(true);
    try {
      const r = await api.get("/showcase/settings");
      setSc({
        enabled: r.data.enabled,
        handle: r.data.handle || r.data.suggested_handle || "",
        title: r.data.title || "",
        bio: r.data.bio || "",
        views: r.data.views || 0,
      });
    } catch {
      setSc({ enabled: false, handle: "", title: "", bio: "" });
    }
    api.get("/showcase/followers").then((r) => setFollowers(r.data.followers || [])).catch(() => setFollowers([]));
  };

  const followedAgo = (iso) => {
    if (!iso) return "";
    return new Date(iso).toLocaleDateString("it-IT", { day: "numeric", month: "short", year: "numeric" });
  };

  const saveShowcase = async () => {
    if (!sc) return;
    setScSaving(true);
    try {
      const r = await api.post("/showcase/settings", sc);
      setSc((s) => ({ ...s, handle: r.data.handle }));
      toast.success(r.data.enabled ? "Vetrina pubblica attiva" : "Vetrina salvata (disattivata)");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Impossibile salvare la vetrina");
    } finally {
      setScSaving(false);
    }
  };

  const showcaseUrl = sc?.handle ? `${window.location.origin}/vetrina/${sc.handle}` : "";

  const sendReply = async (commentId) => {
    const text = (replyDrafts[commentId] || "").trim();
    if (!text || !selected) return;
    setReplyingId(commentId);
    try {
      const r = await api.post(`/outfits/${selected.id}/comments/${commentId}/reply`, { text });
      const apply = (o) => ({
        ...o,
        comments: (o.comments || []).map((c) => (c.id === commentId ? { ...c, reply: r.data.reply } : c)),
      });
      setSelected((s) => apply(s));
      setOutfits((list) => list.map((o) => (o.id === selected.id ? apply(o) : o)));
      setReplyDrafts((d) => ({ ...d, [commentId]: "" }));
      toast.success("Risposta inviata");
    } catch {
      toast.error("Impossibile inviare la risposta");
    } finally {
      setReplyingId(null);
    }
  };

  const remove = async (id, e) => {
    e.stopPropagation();
    try {
      await api.delete(`/outfits/${id}`);
      setOutfits((o) => o.filter((x) => x.id !== id));
      toast.success("Outfit rimosso");
    } catch {
      toast.error("Impossibile rimuovere");
    }
  };

  const downloadOutfitImage = async (id, title) => {
    if (!id) return;
    try {
      const r = await api.get(`/outfits/${id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `mylook-${(title || "look").replace(/\s+/g, "-").toLowerCase()}.png`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Immagine scaricata");
    } catch {
      toast.error("Impossibile scaricare l'immagine");
    }
  };

  const shareOutfit = async (id, channel = "copy", title = "") => {
    try {
      const r = await api.post(`/outfits/${id}/share`);
      const url = `${window.location.origin}/api/share/${r.data.share_id}`;
      const text = `Guarda questo look${title ? ` "${title}"` : ""} su My Look`;
      if (channel === "whatsapp") {
        window.open(`https://wa.me/?text=${encodeURIComponent(text + " " + url)}`, "_blank");
      } else if (channel === "instagram") {
        try { await navigator.clipboard.writeText(url); } catch { /* clipboard blocked */ }
        toast.success("Link copiato! Incollalo nella tua storia o bio Instagram");
        window.open("https://www.instagram.com/", "_blank");
      } else {
        try { await navigator.clipboard.writeText(url); } catch { /* clipboard blocked */ }
        toast.success("Link di condivisione copiato!", {
          action: { label: "Apri", onClick: () => window.open(url, "_blank") },
        });
      }
    } catch {
      toast.error("Impossibile creare il link");
    }
  };

  const toggleFavorite = async (id, current, e) => {
    e.stopPropagation();
    const next = !current;
    setOutfits((o) => {
      const updated = o.map((x) => (x.id === id ? { ...x, favorite: next } : x));
      return [...updated].sort((a, b) => (b.favorite ? 1 : 0) - (a.favorite ? 1 : 0));
    });
    try {
      await api.patch(`/outfits/${id}/favorite`, { favorite: next });
      toast.success(next ? "Aggiunto ai preferiti" : "Rimosso dai preferiti");
    } catch {
      toast.error("Impossibile aggiornare i preferiti");
      setOutfits((o) => o.map((x) => (x.id === id ? { ...x, favorite: current } : x)));
    }
  };

  if (outfits === null)
    return (
      <div className="min-h-[60vh] grid place-items-center">
        <Loader2 className="h-8 w-8 text-[#2f74f0] animate-spin" />
      </div>
    );

  const styleOptions = Array.from(new Set((outfits || []).map((o) => o.style).filter(Boolean)));
  const seasonOptions = Array.from(new Set((outfits || []).map((o) => seasonOf(o.style))));
  const filtered = (outfits || []).filter(
    (o) => (styleFilter === "all" || o.style === styleFilter) &&
           (seasonFilter === "all" || seasonOf(o.style) === seasonFilter)
  );
  const sortedFiltered = sortBy === "views"
    ? [...filtered].sort((a, b) => (b.views || 0) - (a.views || 0))
    : filtered;
  const topLook = (outfits || []).slice().sort(
    (a, b) => (b.likes || 0) - (a.likes || 0) || (b.views || 0) - (a.views || 0)
  )[0];
  const showTop = topLook && ((topLook.likes || 0) > 0 || (topLook.views || 0) > 0);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 lg:py-14">
      <div className="flex items-center gap-3 mb-2">
        <span className="text-xs font-mono uppercase tracking-widest text-[#a9772f]">Collezione salvata</span>
      </div>
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <h1 className="font-serif text-3xl sm:text-4xl font-semibold text-[#2e2015]">Il tuo guardaroba</h1>
          <p className="text-[#6f5c47] mt-2">Gli outfit che hai accettato e salvato.</p>
        </div>
        <Button
          data-testid="showcase-manage-btn"
          onClick={openShowcase}
          variant="outline"
          className="border-[#1e5fe0]/30 text-[#1a4fc0] hover:bg-[#1e5fe0]/10 bg-transparent shrink-0"
        >
          <Globe className="mr-2 h-4 w-4" /> Vetrina pubblica
        </Button>
      </div>

      {outfits.length === 0 ? (
        <div className="mt-16 rounded-xl border border-dashed border-[#1e5fe0]/25 bg-white/70 p-16 text-center">
          <Shirt className="h-12 w-12 text-[#a9772f]/50 mx-auto mb-4" />
          <h2 className="font-serif text-xl text-[#2e2015]">Nessun outfit salvato</h2>
          <p className="text-[#8a7760] mt-2 mb-6">Crea la tua prima proposta nello Studio Stylista.</p>
          <Button
            data-testid="wardrobe-empty-cta"
            onClick={() => navigate("/studio")}
            className="bg-[#1e5fe0] hover:bg-[#2f74f0] text-white font-semibold rounded-full px-6"
          >
            <Sparkles className="mr-2 h-4 w-4" /> Vai allo studio
          </Button>
        </div>
      ) : (
        <>
          {showTop && (
            <div data-testid="look-of-the-month" className="mt-8 rounded-2xl border border-[#a9772f]/30 bg-gradient-to-br from-[#f3ede2] to-[#dbe6ef] p-5 sm:p-6 flex flex-col sm:flex-row items-center gap-5">
              <div className="h-40 w-32 rounded-xl overflow-hidden bg-[#dce6ed] shrink-0 border-2 border-white shadow-lg">
                {topLook.image_path ? (
                  <img src={fileUrl(topLook.image_path)} alt={topLook.title} className="w-full h-full object-cover" />
                ) : (
                  <div className="h-full grid place-items-center"><Shirt className="h-8 w-8 text-[#a9772f]/40" /></div>
                )}
              </div>
              <div className="flex-1 min-w-0 text-center sm:text-left">
                <span className="inline-flex items-center gap-1 text-xs font-mono uppercase tracking-widest text-[#a9772f]">
                  <Star className="h-4 w-4 fill-[#a9772f] text-[#a9772f]" /> Look del mese
                </span>
                <h3 className="font-serif text-2xl text-[#2e2015] mt-1 truncate">{topLook.title}</h3>
                <div className="flex items-center gap-3 mt-2 justify-center sm:justify-start flex-wrap text-sm text-[#6f5c47]">
                  <span className="inline-flex items-center gap-1"><Heart className="h-4 w-4 text-red-500" /> {topLook.likes || 0} mi piace</span>
                  <span className="inline-flex items-center gap-1"><Eye className="h-4 w-4" /> {topLook.views || 0} aperture</span>
                  <span className="text-[#2f74f0] font-mono">€{topLook.total_price}</span>
                </div>
              </div>
              <Button
                data-testid="look-of-the-month-view"
                onClick={() => setSelected(topLook)}
                className="bg-[#1e5fe0] hover:bg-[#2f74f0] text-white font-semibold rounded-full px-6 shrink-0"
              >
                Vedi il look
              </Button>
            </div>
          )}

          {/* Filters */}
          <div data-testid="wardrobe-filters" className="mt-8 flex flex-wrap items-center gap-3">
            <span className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-widest text-[#a9772f]">
              <SlidersHorizontal className="h-4 w-4" /> Filtra
            </span>
            <Select value={styleFilter} onValueChange={setStyleFilter}>
              <SelectTrigger data-testid="wardrobe-filter-style" className="w-44 bg-white border-[#1e5fe0]/20 text-[#2e2015] h-10">
                <SelectValue placeholder="Stile" />
              </SelectTrigger>
              <SelectContent className="bg-white border-[#1e5fe0]/20 text-[#2e2015]">
                <SelectItem value="all">Tutti gli stili</SelectItem>
                {styleOptions.map((s) => (
                  <SelectItem key={s} value={s} className="focus:bg-[#1e5fe0]/15 focus:text-[#1a4fc0]">{s}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={seasonFilter} onValueChange={setSeasonFilter}>
              <SelectTrigger data-testid="wardrobe-filter-season" className="w-44 bg-white border-[#1e5fe0]/20 text-[#2e2015] h-10">
                <SelectValue placeholder="Stagione" />
              </SelectTrigger>
              <SelectContent className="bg-white border-[#1e5fe0]/20 text-[#2e2015]">
                <SelectItem value="all">Tutte le stagioni</SelectItem>
                {seasonOptions.map((s) => (
                  <SelectItem key={s} value={s} className="focus:bg-[#1e5fe0]/15 focus:text-[#1a4fc0]">{s}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={sortBy} onValueChange={setSortBy}>
              <SelectTrigger data-testid="wardrobe-sort" className="w-44 bg-white border-[#1e5fe0]/20 text-[#2e2015] h-10">
                <SelectValue placeholder="Ordina" />
              </SelectTrigger>
              <SelectContent className="bg-white border-[#1e5fe0]/20 text-[#2e2015]">
                <SelectItem value="recent" className="focus:bg-[#1e5fe0]/15 focus:text-[#1a4fc0]">Recenti / Preferiti</SelectItem>
                <SelectItem value="views" className="focus:bg-[#1e5fe0]/15 focus:text-[#1a4fc0]">Più visti dagli amici</SelectItem>
              </SelectContent>
            </Select>
            {(styleFilter !== "all" || seasonFilter !== "all") && (
              <Button
                data-testid="wardrobe-filter-reset"
                variant="ghost"
                onClick={() => { setStyleFilter("all"); setSeasonFilter("all"); }}
                className="h-10 text-[#1a4fc0] hover:bg-[#1e5fe0]/10"
              >
                Azzera filtri
              </Button>
            )}
            <span className="text-xs text-[#8a7760] ml-auto font-mono">{filtered.length} outfit</span>
          </div>

          {sortedFiltered.length === 0 ? (
            <div data-testid="wardrobe-no-results" className="mt-8 rounded-xl border border-dashed border-[#1e5fe0]/25 bg-white/70 p-12 text-center text-[#8a7760]">
              Nessun outfit corrisponde ai filtri selezionati.
            </div>
          ) : (
            <div data-testid="wardrobe-grid" className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-6">
              {sortedFiltered.map((o, i) => (
                <motion.div
                  key={o.id}
                  data-testid="wardrobe-card-item"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.06 }}
                  onClick={() => setSelected(o)}
                  className="group cursor-pointer rounded-xl border border-[#1e5fe0]/20 bg-white/75 overflow-hidden hover:border-[#2f74f0]/50 transition-colors"
                >
                  <div className="relative h-72 bg-[#dce6ed] overflow-hidden">
                    {o.image_path ? (
                      <img src={fileUrl(o.image_path)} alt={o.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                    ) : (
                      <div className="h-full grid place-items-center"><Shirt className="h-10 w-10 text-[#a9772f]/40" /></div>
                    )}
                    <button
                      type="button"
                      data-testid={`wardrobe-favorite-btn-${o.id}`}
                      onClick={(e) => toggleFavorite(o.id, o.favorite, e)}
                      className="absolute top-3 right-3 grid place-items-center h-9 w-9 rounded-full bg-white/90 backdrop-blur border border-[#1e5fe0]/20 shadow hover:bg-white transition-colors"
                      aria-label={o.favorite ? "Rimuovi dai preferiti" : "Aggiungi ai preferiti"}
                    >
                      <Heart className={`h-4 w-4 ${o.favorite ? "fill-red-500 text-red-500" : "text-[#8a7760]"}`} />
                    </button>
                  </div>
                  <div className="p-5">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <h3 className="font-serif text-lg text-[#2e2015] truncate">{o.title}</h3>
                        <div className="flex items-center gap-2 mt-1 flex-wrap">
                          <Badge variant="outline" className="border-[#1e5fe0]/30 text-[#1a4fc0] text-xs">{o.style}</Badge>
                          <Badge variant="outline" className="border-[#a9772f]/30 text-[#a9772f] text-xs">{seasonOf(o.style)}</Badge>
                          <span className="text-[#2f74f0] font-mono text-sm">€{o.total_price}</span>
                          {o.views > 0 && (
                            <span className="inline-flex items-center gap-1 text-xs text-[#8a7760]"><Eye className="h-3 w-3" />{o.views}</span>
                          )}
                          {o.likes > 0 && (
                            <span className="inline-flex items-center gap-1 text-xs text-[#8a7760]"><Heart className="h-3 w-3" />{o.likes}</span>
                          )}
                          {o.views >= 10 && (
                            <span className="inline-flex items-center gap-1 text-xs font-semibold text-[#a9772f] bg-[#a9772f]/15 border border-[#a9772f]/40 rounded-full px-2 py-0.5"><Flame className="h-3 w-3" />Popolare</span>
                          )}
                        </div>
                      </div>
                      <Button
                        data-testid="wardrobe-delete-btn"
                        size="icon"
                        variant="ghost"
                        onClick={(e) => remove(o.id, e)}
                        className="text-[#8a7760] hover:text-red-400 hover:bg-red-500/10 shrink-0"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </>
      )}

      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="bg-[#dce6ed] border-[#1e5fe0]/25 text-[#2e2015] max-w-3xl max-h-[90vh] overflow-y-auto">
          {selected && (
            <>
              <DialogHeader>
                <DialogTitle className="font-serif text-2xl text-[#2e2015]">{selected.title}</DialogTitle>
              </DialogHeader>
              <div className="grid sm:grid-cols-2 gap-6">
                <div className="rounded-lg overflow-hidden border border-[#1e5fe0]/20">
                  {selected.image_path ? (
                    <img src={fileUrl(selected.image_path)} alt={selected.title} className="w-full object-cover" />
                  ) : (
                    <div className="h-64 grid place-items-center"><Shirt className="h-10 w-10 text-[#a9772f]/40" /></div>
                  )}
                </div>
                <div>
                  <p className="text-sm text-[#6f5c47] mb-4">{selected.summary}</p>
                  <div className="flex items-center gap-3 mb-4 flex-wrap">
                    <Badge className="bg-[#1e5fe0]/15 text-[#1e5fe0] border-[#1e5fe0]/30">{selected.style}</Badge>
                    <span className="text-[#2f74f0] font-mono">Totale €{selected.total_price}</span>
                    <span data-testid="wardrobe-views" className="inline-flex items-center gap-1 text-xs text-[#8a7760]">
                      <Eye className="h-3.5 w-3.5" /> {selected.views || 0} aperture
                    </span>
                    <span className="inline-flex items-center gap-1 text-xs text-[#8a7760]">
                      <Heart className="h-3.5 w-3.5" /> {selected.likes || 0} mi piace
                    </span>
                    {selected.views >= 10 && (
                      <span className="inline-flex items-center gap-1 text-xs font-semibold text-[#a9772f] bg-[#a9772f]/15 border border-[#a9772f]/40 rounded-full px-2 py-0.5"><Flame className="h-3 w-3" />Popolare</span>
                    )}
                  </div>
                  <div className="space-y-2">
                    {selected.items.map((it) => (
                      <div key={it.id} className="flex items-center justify-between text-sm border-b border-[#1e5fe0]/10 py-2">
                        <div className="min-w-0">
                          <div className="text-[#3a2a1c] truncate">{it.name}</div>
                          <div className="text-xs text-[#8a7760]">{it.category} · Taglia {it.size} · {it.store}</div>
                        </div>
                        <a href={it.buy_url} target="_blank" rel="noopener noreferrer" className="text-[#2f74f0] hover:text-[#1e5fe0] shrink-0 ml-3">
                          <ExternalLink className="h-4 w-4" />
                        </a>
                      </div>
                    ))}
                  </div>
                  {selected.comments && selected.comments.length > 0 && (
                    <div className="mt-4 border-t border-[#1e5fe0]/10 pt-3">
                      <div className="text-xs font-mono uppercase tracking-widest text-[#a9772f] mb-2">Commenti degli amici</div>
                      <div className="space-y-3 max-h-56 overflow-auto pr-1">
                        {selected.comments.slice().reverse().map((c, i) => (
                          <div key={c.id || i} data-testid="wardrobe-comment" className="text-sm">
                            <div>
                              <span className="font-medium text-[#3a2a1c]">{c.author || "Anonimo"}:</span>{" "}
                              <span className="text-[#4d3a2a]">{c.text}</span>
                            </div>
                            {c.reply ? (
                              <div data-testid="wardrobe-comment-reply" className="mt-1 ml-3 pl-2 border-l-2 border-[#a9772f]/40">
                                <span className="text-xs font-semibold text-[#a9772f] inline-flex items-center gap-1">
                                  <CornerDownRight className="h-3 w-3" /> La tua risposta:
                                </span>{" "}
                                <span className="text-[#4d3a2a]">{c.reply.text}</span>
                              </div>
                            ) : c.id ? (
                              <div className="mt-1.5 flex items-center gap-2">
                                <Input
                                  data-testid={`wardrobe-reply-input-${c.id}`}
                                  value={replyDrafts[c.id] || ""}
                                  onChange={(e) => setReplyDrafts((d) => ({ ...d, [c.id]: e.target.value }))}
                                  placeholder="Rispondi…"
                                  className="h-8 text-sm bg-white border-[#1e5fe0]/20 text-[#2e2015]"
                                />
                                <Button
                                  data-testid={`wardrobe-reply-send-${c.id}`}
                                  size="icon"
                                  onClick={() => sendReply(c.id)}
                                  disabled={replyingId === c.id || !(replyDrafts[c.id] || "").trim()}
                                  className="h-8 w-8 bg-[#1e5fe0] hover:bg-[#2f74f0] text-white shrink-0"
                                >
                                  {replyingId === c.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
                                </Button>
                              </div>
                            ) : null}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  <div className="mt-5 flex gap-3">
                    <Button
                      className="flex-1 bg-[#1e5fe0] hover:bg-[#2f74f0] text-white font-semibold"
                      onClick={() => window.open(selected.items[0]?.buy_url, "_blank")}
                    >
                      <ShoppingBag className="mr-2 h-4 w-4" /> Acquisti
                    </Button>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          data-testid="wardrobe-share-btn"
                          variant="outline"
                          className="border-[#a9772f]/40 text-[#a9772f] hover:bg-[#a9772f]/10 bg-transparent"
                        >
                          <Share2 className="mr-2 h-4 w-4" /> Condividi
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="bg-white border-[#1e5fe0]/20 text-[#2e2015]">
                        <DropdownMenuItem data-testid="wardrobe-share-whatsapp" onClick={() => shareOutfit(selected.id, "whatsapp", selected.title)} className="cursor-pointer focus:bg-[#1e5fe0]/10">
                          <MessageCircle className="mr-2 h-4 w-4 text-emerald-600" /> WhatsApp
                        </DropdownMenuItem>
                        <DropdownMenuItem data-testid="wardrobe-share-instagram" onClick={() => shareOutfit(selected.id, "instagram", selected.title)} className="cursor-pointer focus:bg-[#1e5fe0]/10">
                          <Instagram className="mr-2 h-4 w-4 text-pink-600" /> Instagram
                        </DropdownMenuItem>
                        <DropdownMenuItem data-testid="wardrobe-share-copy" onClick={() => shareOutfit(selected.id, "copy", selected.title)} className="cursor-pointer focus:bg-[#1e5fe0]/10">
                          <Link2 className="mr-2 h-4 w-4 text-[#1e5fe0]" /> Copia link
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                    <Button
                      data-testid="wardrobe-download-btn"
                      variant="outline"
                      size="icon"
                      title="Salva immagine"
                      className="border-[#1e5fe0]/30 text-[#1a4fc0] hover:bg-[#1e5fe0]/10 bg-transparent shrink-0"
                      onClick={() => downloadOutfitImage(selected.id, selected.title)}
                    >
                      <Download className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={showcaseOpen} onOpenChange={setShowcaseOpen}>
        <DialogContent data-testid="showcase-dialog" className="bg-[#dce6ed] border-[#1e5fe0]/25 text-[#2e2015] max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl text-[#2e2015] flex items-center gap-2">
              <Globe className="h-5 w-5 text-[#2f74f0]" /> Vetrina pubblica
            </DialogTitle>
            <DialogDescription className="text-[#6f5c47]">
              Crea una pagina con i tuoi look più amati, condivisibile con un solo link.
            </DialogDescription>
          </DialogHeader>
          {sc && (
            <div className="space-y-5">
              <div className="flex items-center justify-between rounded-lg border border-[#1e5fe0]/20 bg-white/70 px-4 py-3">
                <div>
                  <div className="font-medium text-[#3a2a1c]">Vetrina attiva</div>
                  <div className="text-xs text-[#8a7760]">Se disattivata, il link non sarà accessibile.</div>
                </div>
                <Switch
                  data-testid="showcase-enabled-switch"
                  checked={sc.enabled}
                  onCheckedChange={(v) => setSc((s) => ({ ...s, enabled: v }))}
                />
              </div>

              {sc.enabled && (
                <div data-testid="showcase-views-stat" className="flex items-center gap-3 rounded-lg border border-[#a9772f]/30 bg-[#a9772f]/10 px-4 py-3">
                  <span className="grid place-items-center h-9 w-9 rounded-full bg-[#a9772f] text-white shrink-0">
                    <Eye className="h-4 w-4" />
                  </span>
                  <div>
                    <div className="font-serif text-xl text-[#8a5f22] leading-none">{sc.views || 0}</div>
                    <div className="text-xs text-[#a9772f]">visite alla tua vetrina</div>
                  </div>
                </div>
              )}

              {sc.enabled && (
                <div data-testid="showcase-followers" className="rounded-lg border border-[#1e5fe0]/20 bg-white/70 px-4 py-3">
                  <div className="flex items-center gap-2 text-sm font-medium text-[#3a2a1c]">
                    <UserCheck className="h-4 w-4 text-[#2f74f0]" /> Chi ti segue
                    <span className="ml-auto text-xs font-mono text-[#8a7760]">{followers.length}</span>
                  </div>
                  {followers.length === 0 ? (
                    <p className="text-xs text-[#8a7760] mt-2">Ancora nessun follower. Condividi il link della tua vetrina!</p>
                  ) : (
                    <div className="mt-3 space-y-2 max-h-40 overflow-auto">
                      {followers.map((f, i) => (
                        <div key={i} data-testid="follower-item" className="flex items-center gap-2.5">
                          <span className="grid place-items-center h-7 w-7 rounded-full bg-[#1e5fe0]/10 text-[#1e5fe0] text-xs font-bold shrink-0">
                            {(f.name || "U").charAt(0).toUpperCase()}
                          </span>
                          {f.handle ? (
                            <button
                              data-testid="follower-open-vetrina"
                              onClick={() => window.open(`/vetrina/${f.handle}`, "_blank")}
                              className="text-sm text-[#1a4fc0] hover:underline truncate flex-1 text-left inline-flex items-center gap-1"
                              title="Apri la sua vetrina"
                            >
                              {f.name || "Utente"} <ExternalLink className="h-3 w-3 shrink-0" />
                            </button>
                          ) : (
                            <span className="text-sm text-[#3a2a1c] truncate flex-1">{f.name || "Utente"}</span>
                          )}
                          <span className="text-xs text-[#8a7760] shrink-0">{followedAgo(f.created_at)}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              <div>
                <label className="text-sm font-medium text-[#3a2a1c]">Nome pubblico (handle)</label>
                <div className="mt-1.5 flex items-center rounded-md border border-[#1e5fe0]/20 bg-white overflow-hidden">
                  <span className="pl-3 pr-1 text-sm text-[#8a7760] shrink-0">/vetrina/</span>
                  <Input
                    data-testid="showcase-handle-input"
                    value={sc.handle}
                    onChange={(e) => setSc((s) => ({ ...s, handle: e.target.value.toLowerCase() }))}
                    placeholder="il-tuo-nome"
                    className="border-0 focus-visible:ring-0 bg-transparent text-[#2e2015]"
                  />
                </div>
                <div className="text-xs text-[#8a7760] mt-1">Solo lettere, numeri e trattini (min. 3 caratteri).</div>
              </div>

              <div>
                <label className="text-sm font-medium text-[#3a2a1c]">Titolo</label>
                <Input
                  data-testid="showcase-title-input"
                  value={sc.title}
                  onChange={(e) => setSc((s) => ({ ...s, title: e.target.value }))}
                  placeholder="La mia vetrina di look"
                  className="mt-1.5 bg-white border-[#1e5fe0]/20 text-[#2e2015]"
                />
              </div>

              <div>
                <label className="text-sm font-medium text-[#3a2a1c]">Bio / descrizione</label>
                <Textarea
                  data-testid="showcase-bio-input"
                  value={sc.bio}
                  onChange={(e) => setSc((s) => ({ ...s, bio: e.target.value }))}
                  placeholder="Due parole su di te e sul tuo stile…"
                  className="mt-1.5 bg-white border-[#1e5fe0]/20 text-[#2e2015] min-h-[70px]"
                />
              </div>

              {sc.enabled && showcaseUrl && (
                <div className="flex items-center gap-2 rounded-lg bg-[#1e5fe0]/10 border border-[#1e5fe0]/20 px-3 py-2">
                  <span data-testid="showcase-url" className="text-sm text-[#1a4fc0] truncate flex-1">{showcaseUrl}</span>
                  <Button
                    data-testid="showcase-copy-btn"
                    size="icon"
                    variant="ghost"
                    className="h-8 w-8 text-[#1a4fc0] hover:bg-[#1e5fe0]/15 shrink-0"
                    onClick={async () => {
                      try { await navigator.clipboard.writeText(showcaseUrl); toast.success("Link copiato!"); } catch { /* ignore */ }
                    }}
                  >
                    <Copy className="h-4 w-4" />
                  </Button>
                  <Button
                    data-testid="showcase-open-btn"
                    size="icon"
                    variant="ghost"
                    className="h-8 w-8 text-[#1a4fc0] hover:bg-[#1e5fe0]/15 shrink-0"
                    onClick={() => window.open(showcaseUrl, "_blank")}
                  >
                    <ExternalLink className="h-4 w-4" />
                  </Button>
                </div>
              )}

              <Button
                data-testid="showcase-save-btn"
                onClick={saveShowcase}
                disabled={scSaving}
                className="w-full bg-[#1e5fe0] hover:bg-[#2f74f0] text-white font-semibold h-11"
              >
                {scSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Salva vetrina"}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
